import { UsageEventsTracker } from './UsageEventsTracker';
import { registerErrorReportingService } from './errorReportingBridge';

/**
 * ErrorReportingService
 * Global error capture system that reports all frontend errors through the
 * existing UsageEventsTracker → generic_usage_event pipeline.
 *
 * Captures: window.onerror, unhandled promise rejections, React error boundary
 * catches, and console.error intercepts. Each error includes rich context:
 * stack trace, current URL/route, browser info, session ID, severity,
 * and breadcrumbs (recent user actions before the error).
 *
 * Deduplication prevents flood: max 1 report per error fingerprint per 60s,
 * max 50 unique errors per session.
 */

const DEDUP_WINDOW_MS = 60_000; // 60 seconds per fingerprint
const MAX_UNIQUE_PER_SESSION = 50;
const MAX_BREADCRUMBS = 20;

// Patterns to ignore (React internals, noisy browser messages, expected behavior)
const IGNORE_PATTERNS = [
  /^Warning:/,
  /^React does not recognize/,
  /^Each child in a list should have a unique/,
  /^Can't perform a React state update on an unmounted/,
  /^ResizeObserver loop/,
  /^Loading chunk \d+ failed/,
  /^%c/,                     // styled console output from React DevTools
  /^Download the React DevTools/,
  /AuthService: Login failed/,                          // wrong credentials — user error, not app error
  /session expired, redirecting to login/,              // expected auth flow when token expires
  /No refresh token or refresh failed/,                 // expected when session is stale
  /webkit\.messageHandlers/i,                            // Safari WKWebView bridge probe — not our code
];

// Third-party resource URLs we expect ad blockers / firewalls to block
const IGNORED_RESOURCE_PATTERNS = [
  /googletagmanager\.com/,
  /google-analytics\.com/,
  /gtag\/js/,
  /js\.stripe\.com/,
  /facebook\.net/,
  /connect\.facebook/,
  /doubleclick\.net/,
  /hotjar\.com/,
];

function shouldIgnore(message) {
  if (!message) return false;
  const str = typeof message === 'string' ? message : String(message);
  return IGNORE_PATTERNS.some(p => p.test(str));
}

function fingerprint(message, source, lineno) {
  const msg = (typeof message === 'string' ? message : String(message || '')).slice(0, 200);
  const src = (source || '').split('/').pop() || '';
  return `${msg}::${src}::${lineno || 0}`;
}

function collectBrowserContext() {
  // Capture minimal browser info — avoid full userAgent (PII-adjacent)
  const ua = navigator.userAgent || '';
  const browserSnippet = ua.length > 80 ? ua.slice(0, 80) + '…' : ua;
  return {
    url: window.location.pathname + window.location.search,
    route: window.location.pathname,
    browser: browserSnippet,
    language: navigator.language,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    timestamp: new Date().toISOString(),
  };
}

class _ErrorReportingService {
  constructor() {
    this._initialized = false;
    this._breadcrumbs = [];
    this._dedupMap = new Map();    // fingerprint → lastReportedMs
    this._uniqueCount = 0;
    this._originalConsoleError = null;
    this._reporting = false;       // re-entrancy guard
  }

  /**
   * Initialize global error handlers. Call once at app startup.
   */
  init() {
    if (this._initialized) return;
    if (typeof window === 'undefined') return;
    this._initialized = true;
    registerErrorReportingService(this);

    // 1. window.onerror — uncaught JS errors
    window.onerror = (message, source, lineno, colno, error) => {
      this._handleError({
        error,
        message: message || error?.message,
        source,
        lineno,
        colno,
        severity: 'error',
        captureSource: 'window_onerror',
      });
      // Don't suppress the error — let it also appear in the console
      return false;
    };

    // 2. Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason;
      this._handleError({
        error: error instanceof Error ? error : null,
        message: error?.message || String(error),
        stack: error?.stack,
        severity: 'error',
        captureSource: 'unhandled_rejection',
      });
    });

    // 3. Resource loading errors (broken images, failed scripts/CSS)
    // These fire on the element, not on window.onerror, so we need capture phase
    document.addEventListener('error', (event) => {
      const target = event.target;
      if (!target || target === window || target === document) return;
      // Only report resource load failures (img, script, link)
      const tagName = (target.tagName || '').toLowerCase();
      if (!['img', 'script', 'link'].includes(tagName)) return;
      const src = target.src || target.href || '';
      // Skip third-party resources that ad blockers commonly block
      if (IGNORED_RESOURCE_PATTERNS.some(p => p.test(src))) return;
      this._handleError({
        message: `Failed to load ${tagName}: ${src}`,
        source: src,
        severity: tagName === 'script' ? 'error' : 'warning',
        captureSource: 'resource_error',
        extraMetadata: { tagName, resourceUrl: src },
      });
    }, true); // capture phase

    // 4. console.error interception
    this._originalConsoleError = console.error;
    // eslint-disable-next-line no-console
    console.error = (...args) => {
      // Always call original first so dev tools are not broken
      this._originalConsoleError.apply(console, args);

      // Build message from args
      const message = args.map(a =>
        a instanceof Error ? a.message : (typeof a === 'string' ? a : '')
      ).join(' ').trim();

      if (!message || shouldIgnore(message)) return;

      // Extract error object if first arg is an Error
      const errorObj = args.find(a => a instanceof Error) || null;

      this._handleError({
        error: errorObj,
        message,
        stack: errorObj?.stack,
        severity: 'warning',
        captureSource: 'console_error',
      });
    };
  }

  /**
   * Manually capture an error (for use in catch blocks).
   * @param {Error} error
   * @param {object} [context] - Additional context (componentStack, source, metadata)
   */
  captureError(error, context = {}) {
    if (!error) return;
    this._handleError({
      error,
      message: error.message,
      stack: error.stack,
      severity: context.severity || 'error',
      captureSource: context.source || 'manual',
      componentStack: context.componentStack,
      extraMetadata: context.metadata,
    });
  }

  /**
   * Manually capture a message-level error.
   * @param {string} message
   * @param {'critical'|'error'|'warning'} severity
   * @param {object} [context]
   */
  captureMessage(message, severity = 'warning', context = {}) {
    this._handleError({
      message,
      severity,
      captureSource: context.source || 'manual_message',
      extraMetadata: context.metadata,
    });
  }

  /**
   * Add a breadcrumb for pre-error context.
   * @param {string} category - e.g., 'navigation', 'click', 'api', 'state'
   * @param {string} message - Human-readable description
   * @param {object} [data] - Additional data
   */
  addBreadcrumb(category, message, data) {
    this._breadcrumbs.push({
      category,
      message,
      data: data || undefined,
      timestamp: new Date().toISOString(),
    });
    if (this._breadcrumbs.length > MAX_BREADCRUMBS) {
      this._breadcrumbs.shift();
    }
  }

  // ─── Internal ────────────────────────────────────────────

  _handleError({ error, message, source, lineno, colno, stack, severity, captureSource, componentStack, extraMetadata }) {
    if (this._reporting) return; // prevent recursion if tracker calls console.error
    const msg = message || error?.message || 'Unknown error';
    if (shouldIgnore(msg)) return;

    // Deduplication check
    const fp = fingerprint(msg, source || error?.fileName, lineno || this._extractLine(error?.stack));
    const now = Date.now();
    const lastReported = this._dedupMap.get(fp);
    if (lastReported && (now - lastReported) < DEDUP_WINDOW_MS) return;

    // Session limit check
    if (!lastReported) {
      if (this._uniqueCount >= MAX_UNIQUE_PER_SESSION) return;
      this._uniqueCount++;
    }
    this._dedupMap.set(fp, now);

    // Build payload
    const browser = collectBrowserContext();
    const errorType = error?.name || (error?.constructor?.name !== 'Object' ? error?.constructor?.name : null) || 'Error';
    const stackTrace = stack || error?.stack || null;

    const eventType = componentStack ? 'APP_ERROR_BOUNDARY' : 'APP_ERROR';

    const payload = {
      action: 'error',
      category: 'error_reporting',
      entityType: errorType,
      entityId: fp,
      label: msg.slice(0, 300),
      value: null,
      metadata: {
        errorMessage: msg.slice(0, 2000),
        errorType,
        severity,
        captureSource,
        stackTrace: stackTrace ? stackTrace.slice(0, 5000) : null,
        componentStack: componentStack ? componentStack.slice(0, 3000) : null,
        source: source || null,
        lineno: lineno || null,
        colno: colno || null,
        fingerprint: fp,
        breadcrumbs: [...this._breadcrumbs],
        browser,
        ...(extraMetadata || {}),
      },
    };

    try {
      this._reporting = true;
      UsageEventsTracker.recordUserAction(eventType, payload, { forceFlush: true });
    } catch {
      // If the tracker itself fails, don't recurse — just silently drop
    } finally {
      this._reporting = false;
    }
  }

  _extractLine(stack) {
    if (!stack) return 0;
    const match = stack.match(/:(\d+):\d+/);
    return match ? parseInt(match[1], 10) : 0;
  }

  /**
   * Tear down global handlers (useful for tests).
   */
  destroy() {
    if (!this._initialized) return;
    window.onerror = null;
    // Can't easily remove the unhandledrejection listener without a ref,
    // but resetting _initialized prevents double-init.
    if (this._originalConsoleError) {
      // eslint-disable-next-line no-console
      console.error = this._originalConsoleError;
      this._originalConsoleError = null;
    }
    this._initialized = false;
    this._breadcrumbs = [];
    this._dedupMap.clear();
    this._uniqueCount = 0;
  }
}

const ErrorReportingService = new _ErrorReportingService();
export default ErrorReportingService;
